/** VistoeraTASK-00595 — Generate Image aspect-ratio card picker.
 *
 * Replaces the plain text Select menu with a graphical card grid while
 * reusing ``CanvasSelect`` (body portal + pan/zoom reposition) and the
 * existing ``fields.ratio`` / ``FREE_RATIO`` / ``ratioForSubmit`` contract.
 * No Provider / Worker / task-payload changes.
 */

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CanvasSelect from '../CanvasSelect'
import { FIXED_RATIO_VALUES, FREE_RATIO } from '../imageGenerationRatio'

export interface ImageAspectRatioSelectProps {
  value: string
  onChange: (next: string) => void
  disabled?: boolean
}

type RatioCardDef = {
  value: string
  /** Fixed ratios use "w:h"; free uses FREE_RATIO. */
  shape: 'free' | string
  titleKey: string
  descKey: string
  /** Optional hardcoded title when the option label is the ratio itself. */
  titleLiteral?: string
}

const RATIO_CARDS: RatioCardDef[] = [
  { value: FREE_RATIO, shape: 'free', titleKey: 'generation.ratioCard.freeTitle', descKey: 'generation.ratioCard.freeDesc' },
  { value: '1:1', shape: '1:1', titleLiteral: '1:1', titleKey: '', descKey: 'generation.ratioCard.square' },
  { value: '4:3', shape: '4:3', titleLiteral: '4:3', titleKey: '', descKey: 'generation.ratioCard.landscape' },
  { value: '3:4', shape: '3:4', titleLiteral: '3:4', titleKey: '', descKey: 'generation.ratioCard.portrait' },
  { value: '16:9', shape: '16:9', titleLiteral: '16:9', titleKey: '', descKey: 'generation.ratioCard.widescreen' },
  { value: '9:16', shape: '9:16', titleLiteral: '9:16', titleKey: '', descKey: 'generation.ratioCard.vertical' },
]

/** True aspect-ratio outline inside a fixed icon box (never stretched to square). */
export function RatioShapeIcon({ shape, size = 40 }: { shape: string; size?: number }) {
  const pad = 6
  const max = size - pad * 2
  if (shape === 'free') {
    return (
      <svg
        className="pcv-image-ratio-shape"
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        aria-hidden
      >
        <rect
          x={pad}
          y={pad}
          width={max}
          height={max}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeDasharray="3.5 2.5"
          rx={2}
        />
        <path
          d={`M${pad + 5} ${size - pad - 5} L${size - pad - 5} ${pad + 5}`}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.4}
          strokeLinecap="round"
        />
        <path
          d={`M${pad + 5} ${pad + 11} L${pad + 5} ${pad + 5} L${pad + 11} ${pad + 5}`}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.4}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d={`M${size - pad - 11} ${size - pad - 5} L${size - pad - 5} ${size - pad - 5} L${size - pad - 5} ${size - pad - 11}`}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.4}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )
  }

  const [rwRaw, rhRaw] = shape.split(':')
  const rw = Number(rwRaw) || 1
  const rh = Number(rhRaw) || 1
  let w: number
  let h: number
  if (rw >= rh) {
    w = max
    h = Math.max(6, (max * rh) / rw)
  } else {
    h = max
    w = Math.max(6, (max * rw) / rh)
  }
  const x = (size - w) / 2
  const y = (size - h) / 2
  return (
    <svg
      className="pcv-image-ratio-shape"
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden
    >
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        rx={1.5}
      />
    </svg>
  )
}

export default function ImageAspectRatioSelect({ value, onChange, disabled }: ImageAspectRatioSelectProps) {
  const { t } = useTranslation('canvas')
  const [open, setOpen] = useState(false)

  const options = useMemo(
    () => [
      { value: FREE_RATIO, label: t('generation.ratioFree') },
      ...FIXED_RATIO_VALUES.map((v) => ({ value: v, label: v })),
    ],
    [t],
  )

  return (
    <CanvasSelect
      size="small"
      className="pcv-gen-field-select nodrag nowheel"
      disabled={disabled}
      open={open}
      onOpenChange={setOpen}
      value={value}
      options={options}
      // Cards replace the menu; keep options only for the closed-trigger label.
      popupMatchSelectWidth={false}
      popupClassName="pcv-image-ratio-picker-dropdown"
      styles={{ popup: { root: { padding: 0, minWidth: 332 } } }}
      dropdownRender={() => (
        <div
          className="pcv-image-ratio-picker nodrag nowheel nopan"
          data-testid="image-aspect-ratio-picker"
          // Keep Select open until the card click commits (antd classic).
          onMouseDown={(e) => e.preventDefault()}
          onWheel={(e) => e.stopPropagation()}
        >
          {RATIO_CARDS.map((card) => {
            const selected = value === card.value
            const title = card.titleLiteral || t(card.titleKey)
            const desc = t(card.descKey)
            return (
              <button
                key={card.value}
                type="button"
                className={`pcv-image-ratio-card nodrag${selected ? ' pcv-image-ratio-card--selected' : ''}`}
                data-testid={`image-aspect-ratio-card-${card.value}`}
                data-selected={selected ? 'yes' : 'no'}
                aria-pressed={selected}
                onClick={() => {
                  onChange(card.value)
                  setOpen(false)
                }}
              >
                <span className="pcv-image-ratio-card-icon">
                  <RatioShapeIcon shape={card.shape} />
                </span>
                <span className="pcv-image-ratio-card-title">{title}</span>
                <span className="pcv-image-ratio-card-desc">{desc}</span>
              </button>
            )
          })}
        </div>
      )}
    />
  )
}
